import { QueryClient } from "@tanstack/react-query";

import { ApiError, NetworkError } from "@/api/client";

/**
 * The shared query client.
 *
 * Its defaults are tuned for a point of sale, which is a different animal from a
 * dashboard: the same screen is used all day by someone who cannot wait, on a
 * connection that may vanish mid-shift.
 *
 * Kept in its own module rather than beside the provider component so that
 * editing it does not force a full reload during development, and so tests can
 * build an isolated client without importing the React tree.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Product and price data is replicated, not polled. A minute of staleness
      // is invisible to a cashier and saves a great deal of traffic on a mobile
      // connection.
      staleTime: 60_000,
      gcTime: 10 * 60_000,

      // Refetching on every window focus is wrong here: a POS terminal is
      // focused and blurred constantly as the cashier moves between the till and
      // a card machine, and each refetch would stutter the interface.
      refetchOnWindowFocus: false,
      // But do refetch when the connection returns — that is exactly when the
      // cached figures are most likely to be wrong.
      refetchOnReconnect: true,

      retry: (failureCount, error) => {
        // A rejected request is not made right by asking again. Only genuine
        // transport failures and 5xx are worth retrying, and only twice — beyond
        // that the outbox is the correct mechanism, because it can retry safely
        // across hours and process restarts.
        if (error instanceof ApiError) return error.isRetryable && failureCount < 2;
        if (error instanceof NetworkError) return failureCount < 2;
        return false;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },

    mutations: {
      // Mutations are never retried automatically. A blind retry of "complete
      // this sale" is precisely how a customer gets charged twice. Retrying is
      // the outbox's job, and it does so with a stable idempotency key.
      retry: false,
    },
  },
});
